import { Icon, type IconName } from "@/assets/icons/Icon";
import { BrandLockup } from "@/components/brand/BrandMark";
import { ConnectionBadge } from "@/components/dashboard/ConnectionBadge";
import { ExecutionBadge } from "@/components/dashboard/ExecutionBadge";
import { KillSwitch } from "@/components/dashboard/KillSwitch";
import { MarketStatusBadge } from "@/components/dashboard/MarketStatusBadge";
import { Footer } from "@/components/layout/Footer";
import { Button, Divider, IconButton, TextButton } from "@/components/ui";
import { useAuth } from "@/contexts/AuthContext";
import { useMe, usePrimaryAccount } from "@/hooks/api";
import { nextOnboardingStep } from "@/lib/onboarding";
import { CROSSFADE, SPRINGS } from "@/lib/spring";
import type { OnboardingProgress } from "@/types/api";
import { AnimatePresence, useReducedMotion } from "motion/react";
// 요소별 named export(`div`·`nav`…)라 네임스페이스로 받아요.
import * as m from "motion/react-m";
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  /** Path prefix that keeps the tab lit (e.g. every `/settings/*` page). */
  match: string;
}

const NAV: NavItem[] = [
  { to: "/dashboard", label: "대시보드", icon: "home", match: "/dashboard" },
  { to: "/rules", label: "규칙", icon: "template", match: "/rules" },
  { to: "/assistant", label: "어시스턴트", icon: "bubble", match: "/assistant" },
  { to: "/orders", label: "주문", icon: "document", match: "/orders" },
  { to: "/performance", label: "성과", icon: "coins", match: "/performance" },
  { to: "/book", label: "장부", icon: "book", match: "/book" },
  { to: "/market", label: "시장", icon: "search", match: "/market" },
  { to: "/settings/api-key", label: "설정", icon: "filter", match: "/settings" },
];

function navClass(isActive: boolean): string {
  // 헤더 한 줄에 6개 탭 + 배지 4종이 같이 서요. `whitespace-nowrap`/`shrink-0` 이 없으면
  // 남은 폭이 모자랄 때 flex 가 탭을 눌러서 "대시 / 보드" 처럼 글자 단위로 줄바꿈돼요.
  // `apple-pressable` 은 `:active`(= pointer-down)에 눌림을 붙여요. `click` 을
  // 기다리면 눌렀는데 아무 일도 안 일어난 것처럼 느껴집니다.
  const base =
    "apple-pressable flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-2 text-label-1";
  return isActive
    ? `${base} bg-fill-normal text-label-strong`
    : `${base} text-label-alternative hover:bg-fill-alternative hover:text-label-normal`;
}

/**
 * The signed-in chrome: navigation, the connection badge, the always-present
 * kill switch (S4), and the standing footer disclaimer.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { signOut } = useAuth();
  const { data: me } = useMe();
  const { account } = usePrimaryAccount();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const sentinel = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const reduce = useReducedMotion();

  /*
    떠 있는 크롬 아래로 내용이 지나가기 시작했는지만 봐요. 이 한 비트가
    스크롤 엣지 그림자를 켭니다(`styles/apple/materials.css`) — 맨 위에서도
    그림자를 그리면 그건 그냥 테두리고, 크롬이 떠 있다는 사실을 흐려요.

    스크롤 리스너가 아니라 `IntersectionObserver` 예요. 리스너는 `passive` 를
    붙여도 스크롤마다 콜백이 돌지만, 옵저버는 **경계를 지날 때만** 한 번
    깨어납니다 — 여기서 알아야 하는 것도 그 한 번뿐이에요. 관측 대상은
    크롬 위에 놓인 높이 0 의 표식이라, 그게 뷰포트에서 벗어나는 순간이
    "내용이 크롬 아래로 들어갔다" 와 같습니다.
  */
  useEffect(() => {
    const target = sentinel.current;
    if (!target) return;
    const observer = new IntersectionObserver(([entry]) => setScrolled(!entry.isIntersecting), {
      threshold: 0,
    });
    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="flex min-h-dvh flex-col bg-background-normal">
      {/* 키보드 사용자는 매 화면에서 탭 7개와 상태 배지들을 지나야 본문에 닿아요.
          포커스를 받을 때만 나타납니다(`styles/apple/materials.css`). */}
      <a href="#content" className="apple-skip-link" data-print="hide">
        본문으로 건너뛰기
      </a>
      {/* 인쇄물에는 앱 껍데기가 남으면 안 돼요 — 리포트 화면이 `window.print()` 를 부릅니다
          (`styles/index.css` 의 `@media print`). 클래스가 아니라 데이터 속성으로 표시해서,
          레이아웃 클래스를 고칠 때 이 규칙이 조용히 끊어지지 않게 해요.

          반투명 머티리얼 크롬. 불투명 띠는 화면 위쪽 한 줄을 **가져가는데**,
          머티리얼은 그 아래로 내용이 지나가게 둬요.

          `z-30` 이 필수예요 — `backdrop-filter` 는 새 쌓임 문맥을 만들어서,
          z 를 안 주면 본문 카드가 크롬 위로 올라옵니다.

          하드 보더(`border-b`)를 뺐어요. 1px 실선을 그으면 머티리얼이 "바닥에
          붙은 띠" 로 읽혀서 반투명하게 만든 이유가 사라집니다. 대신 내용이
          실제로 밑으로 지나갈 때만 짧게 흐려지는 그림자를 씁니다. */}
      {/* 높이 0 의 관측 표식. sticky 헤더 자신을 관측할 수는 없어요 — 그건
          스크롤해도 뷰포트에 계속 남아 있어서 항상 교차 상태입니다. */}
      <div ref={sentinel} aria-hidden className="h-0" />

      <header
        data-print="hide"
        data-scrolled={scrolled}
        className="apple-chrome apple-material-edge apple-scroll-edge sticky top-0 z-30"
      >
        {/* 1층 — 정체성과 이동. 탭 7개 + 배지 3종 + 버튼 2개는 1060px 한 줄에 들어가지
            않아서, 예전에는 `flex-wrap` 이 배지 뭉치를 둘째 줄로 흘리고 그 줄의 왼쪽 절반이
            통째로 비었어요(사고처럼 보이는 화면). 폭을 다투게 두지 않고 층을 나눕니다. */}
        <div className="mx-auto flex max-w-layout items-center gap-x-4 px-5 py-3">
          <Link to="/dashboard" className="shrink-0 text-label-strong">
            <BrandLockup size={24} />
          </Link>

          <DesktopNav pathname={location.pathname} />

          <div className="ml-auto flex shrink-0 items-center gap-2">
            {/* 로그아웃은 유일한 계정 조치예요. 텍스트만 있으면 눌리는 것인지 안 보여서
                테두리를 줍니다 — 조용하지만 버튼으로 읽혀요. */}
            <span className="hidden md:inline">
              <Button
                variant="outlined"
                color="assistive"
                size="small"
                onClick={() => void signOut()}
              >
                로그아웃
              </Button>
            </span>
            <span className="md:hidden">
              <IconButton
                icon={menuOpen ? "close" : "menu"}
                variant="background"
                size="small"
                onClick={() => setMenuOpen((open) => !open)}
              />
            </span>
          </div>
        </div>

        {/* 2층 — 상태 스트립. 왼쪽은 "지금 어떤 상태인가"(장·연결), 오른쪽은 그 상태에 대한
            유일한 즉시 조치(전체 정지)예요. 우연히 넘친 줄이 아니라 의미가 있는 줄입니다.
            바탕을 한 단 낮춰서 빨간 버튼이 본문의 주 액션보다 앞에 서지 않게 해요. */}
        {/* 바탕을 `fill-alternative`(알파)로 둬요. `bg-background-alternative` 는
            불투명해서, 이 줄 하나가 머티리얼에 구멍을 냅니다 — 위층은 뒤가
            비치는데 아래층은 안 비쳐서 크롬이 두 재질로 갈라져 보였어요. */}
        <div className="border-t border-line-alternative bg-fill-alternative">
          <div className="mx-auto flex max-w-layout items-center gap-3 px-5 py-2">
            {/* `[&>*]:shrink-0` — 배지가 눌리면 한글이 글자 단위로 쪼개져요("연 결 확 인").
                폭이 모자라면 찌그러뜨리지 말고 줄을 바꿉니다. 가로 스크롤로 두면 연결 상태가
                화면 밖으로 나가는데, 그건 이 제품의 전제 조건이라 숨으면 안 돼요.

                폰에서는 배지 넷이 결국 두 줄이 돼요. 배지가 하나씩 도착하는 동안 한 줄이었다가
                두 줄로 접히면 크롬 전체가 한 줄만큼 자라서 본문이 통째로 밀렸어요(390px CLS
                실측). 두 줄 높이(`min-h-12`)를 처음부터 잡아 둡니다. */}
            <div className="flex min-h-12 min-w-0 flex-wrap items-center gap-2 sm:min-h-0 [&>*]:shrink-0">
              <MarketStatusBadge />
              {/* 좁은 폭에서는 줄바꿈 자체가 구분선 역할을 해요. */}
              <span className="hidden h-4 sm:inline-flex">
                <Divider vertical />
              </span>
              <ConnectionBadge />
              <span className="hidden h-4 sm:inline-flex">
                <Divider vertical />
              </span>
              <ExecutionBadge />
            </div>
            <div className="ml-auto shrink-0">
              <KillSwitch hasAccount={account !== null} />
            </div>
          </div>
        </div>

        {/*
          열림·닫힘이 같은 축(높이)을 왕복해요 — 위에서 펼쳐졌으니 위로 접힙니다.
          스프링을 쓰는 이유는 길이가 아니라 **중단 가능성**입니다: 접히는 중에
          다시 누르면 진행 중인 높이에서 이어서 펼쳐져요. 고정 길이 트랜지션은
          닫힘을 끝낸 뒤에 열기를 시작해서, 두 번 누른 사람이 기다리게 됩니다.

          튐이 없는(`SPRINGS.ui`) 쪽을 쓰는 건 이게 손으로 끌어 여는 물건이
          아니라 버튼으로 여는 메뉴라서예요 — 운동량이 실리지 않은 전환이
          넘어가면 이유 없이 출렁입니다.

          `overflow-hidden` 은 높이 애니메이션에 필수예요. 없으면 접히는 중에
          링크가 크롬 밖으로 삐져나옵니다.
        */}
        <AnimatePresence initial={false}>
          {menuOpen ? (
            <m.nav
              key="mobile-nav"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={reduce ? CROSSFADE : SPRINGS.ui}
              className="overflow-hidden border-t border-line-alternative md:hidden"
            >
              <div className="flex flex-col gap-1 px-5 py-3">
                {NAV.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setMenuOpen(false)}
                    className={`relative ${navClass(location.pathname.startsWith(item.match))}`}
                  >
                    {/* 손에 든 목록에서도 현재 위치는 같은 블루 줄기예요 — 세로로 섭니다. */}
                    {location.pathname.startsWith(item.match) ? (
                      <span
                        aria-hidden="true"
                        className="absolute inset-y-2 left-0 w-1 rounded-xs bg-primary"
                      />
                    ) : null}
                    <Icon name={item.icon} size={16} />
                    {item.label}
                  </NavLink>
                ))}
                <Divider spacing={8} />
                <Button
                  variant="text"
                  color="assistive"
                  size="small"
                  onClick={() => void signOut()}
                >
                  로그아웃
                </Button>
              </div>
            </m.nav>
          ) : null}
        </AnimatePresence>
      </header>

      <main id="content" className="mx-auto w-full max-w-layout flex-1 px-5 py-8">
        {me ? <OnboardingReminder onboarding={me.onboarding} /> : null}
        <SectionFade section={location.pathname.split("/")[1] ?? ""} reduce={reduce === true}>
          {children}
        </SectionFade>
      </main>

      <Footer />
    </div>
  );
}

/*
  셸이 한 번이라도 그려졌는가. 첫 화면은 페이드하지 않아요 — 불투명도 0 으로 시작하면
  그동안 본문이 LCP 후보에서 빠져서 첫 페인트가 그만큼 늦게 잡힙니다. 셸은 코드 분할
  청크를 받는 동안 다시 마운트될 수 있어서(`lastStem` 과 같은 이유) 모듈에 둡니다.
*/
let shellPainted = false;

/**
 * 섹션이 바뀔 때 본문이 짧게 교차해 들어와요 — 줄기가 새 탭으로 미끄러지는 것과 같은 순간이라
 * 둘이 한 동작으로 읽힙니다. 하드 컷이면 위의 줄기만 움직이고 본문은 순간이동해요.
 *
 * **불투명도만** 움직여요. `transform` 을 주면 이 상자가 `position: fixed` 자손(다이얼로그·
 * 바닥 고정 저장 줄)의 기준 상자가 되어서 그것들이 엉뚱한 자리에 붙습니다. 나가는 쪽은
 * 기다리지 않아요(exit 없음) — 전환이 다음 화면을 붙잡으면 안 됩니다.
 *
 * 키는 경로 첫 마디(섹션)예요. `/market` ↔ `/market/005930` 처럼 같은 화면 안에서 주소만
 * 바뀌는 경우까지 키로 쓰면 그 화면이 다시 마운트되어 입력 중인 검색어가 날아가요.
 */
function SectionFade({
  section,
  reduce,
  children,
}: {
  section: string;
  reduce: boolean;
  children: ReactNode;
}) {
  const animate = shellPainted;
  useEffect(() => {
    shellPainted = true;
  }, []);
  return (
    <m.div
      key={section}
      initial={animate ? { opacity: 0 } : false}
      animate={{ opacity: 1 }}
      transition={reduce ? CROSSFADE : { duration: 0.22, ease: "easeOut" }}
    >
      {children}
    </m.div>
  );
}

/** 줄기 막대의 기본 폭(`styles/tranche.css` 의 `.tranche-stem` 과 같은 값). */
const STEM_BASE = 100;
/** 줄기를 탭 글자 폭에 맞추려고 양옆에서 덜어 내는 여백. 탭의 `px-2` 와 같아요. */
const STEM_INSET = 8;

interface TabBox {
  x: number;
  w: number;
}

/*
  마지막 줄기 위치. 처음 가는 화면은 코드 분할 청크를 받는 동안 셸이 다시 마운트될 수
  있어서(최상위 `Suspense`), 컴포넌트 상태에만 두면 줄기가 새 탭에서 **그냥 나타나요** —
  미끄러짐이 처음 방문에서만 사라지는 셈입니다. 모듈에 하나 두고 다음 마운트의 출발점으로
  씁니다.
*/
let lastStem: { x: number; scaleX: number } | null = null;

function stemTarget(box: TabBox | undefined): { x: number; scaleX: number } | null {
  if (!box || box.w === 0) return null;
  return { x: box.x + STEM_INSET, scaleX: Math.max(box.w - STEM_INSET * 2, 0) / STEM_BASE };
}

/**
 * 데스크톱 탭 + 줄기(`DESIGN.md` 시그니처).
 *
 * 현재 화면 아래에 브랜드 블루 막대가 꽂혀요 — 마크의 세로획, "지금 집행 중인 조각" 이에요.
 * 화면을 옮기면 그 막대가 새 탭으로 **미끄러져** 가서 튐 없이 앉습니다(`SPRINGS.ui`).
 * 포인터가 다른 탭에 닿으면 옅은 그림자 줄기가 먼저 그 아래로 가요 — 색만 바뀌는 hover 는
 * "옮길 수 있다" 를 말하지 않습니다.
 *
 * 움직이는 건 `transform`(x·scaleX)뿐이에요. `layout`/`layoutId` 는 `domMax` 가 필요해서
 * 이 앱에서 쓰지 않고(`lib/motion-features.ts`), 위치는 탭의 `offsetLeft`·`offsetWidth` 를
 * 재서 정합니다. 폭은 글꼴 로드·창 크기에 따라 바뀌므로 `ResizeObserver` 가 다시 잽니다 —
 * 스크롤 리스너가 아니라 크기가 바뀔 때만 깨어나요.
 */
function DesktopNav({ pathname }: { pathname: string }) {
  const reduce = useReducedMotion();
  const navRef = useRef<HTMLElement>(null);
  const tabRefs = useRef<Array<HTMLAnchorElement | null>>([]);
  const [boxes, setBoxes] = useState<TabBox[]>([]);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const activeIndex = NAV.findIndex((item) => pathname.startsWith(item.match));

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const measure = () =>
      setBoxes(
        tabRefs.current.map((tab) =>
          tab ? { x: tab.offsetLeft, w: tab.offsetWidth } : { x: 0, w: 0 }
        )
      );
    measure();
    // jsdom(테스트)에는 없어요. 없으면 첫 측정값으로 버팁니다.
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(nav);
    return () => observer.disconnect();
  }, []);

  const stem = stemTarget(boxes[activeIndex]);
  const ghost = stemTarget(hoverIndex === null ? undefined : boxes[hoverIndex]);
  const showGhost = ghost !== null && hoverIndex !== activeIndex;

  useEffect(() => {
    if (stem) lastStem = stem;
  }, [stem]);

  return (
    <nav
      ref={navRef}
      aria-label="주요 화면"
      // `-my-3 self-stretch` — 줄기가 크롬 1층의 **바닥**(상태 스트립과 만나는 선)에 꽂히도록
      // 행의 세로 여백까지 nav 가 차지해요.
      className="relative -my-3 hidden min-w-0 shrink items-center gap-1 self-stretch md:flex"
      onPointerLeave={() => setHoverIndex(null)}
    >
      {NAV.map((item, index) => (
        <NavLink
          key={item.to}
          to={item.to}
          ref={(node) => {
            tabRefs.current[index] = node;
          }}
          onPointerEnter={() => setHoverIndex(index)}
          onFocus={() => setHoverIndex(index)}
          onBlur={() => setHoverIndex(null)}
          aria-current={index === activeIndex ? "page" : undefined}
          className={desktopTabClass(index === activeIndex)}
        >
          <Icon name={item.icon} size={16} />
          {item.label}
        </NavLink>
      ))}

      <m.span
        aria-hidden="true"
        className="tranche-stem tranche-stem-ghost bg-fill-strong"
        initial={false}
        animate={
          ghost ? { x: ghost.x, scaleX: ghost.scaleX, opacity: showGhost ? 1 : 0 } : { opacity: 0 }
        }
        transition={reduce ? CROSSFADE : SPRINGS.ui}
      />
      {stem ? (
        <m.span
          aria-hidden="true"
          className="tranche-stem bg-primary"
          initial={lastStem ?? { x: stem.x, scaleX: stem.scaleX }}
          animate={{ x: stem.x, scaleX: stem.scaleX }}
          transition={reduce ? { duration: 0 } : SPRINGS.move}
        />
      ) : null}
    </nav>
  );
}

function desktopTabClass(isActive: boolean): string {
  // 현재 탭은 바탕 알약이 아니라 **줄기**로 표시해요 — 알약과 줄기를 같이 쓰면 표시가 둘이
  // 되어 어느 쪽이 "여기" 인지 다시 읽어야 합니다. 글자 무게는 바꾸지 않아요(폭이 변하면
  // 줄기가 잰 자리와 어긋납니다).
  const base =
    "apple-pressable flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-2 text-label-1 transition-colors duration-fast ease-standard";
  return isActive
    ? `${base} text-label-strong`
    : `${base} text-label-alternative hover:text-label-normal`;
}

/** 남은 설정 단계를 순서대로 하나만 안내해요. 단계 선택은 `lib/onboarding.ts` 가 합니다. */
function OnboardingReminder({ onboarding }: { onboarding: OnboardingProgress }) {
  const next = nextOnboardingStep(onboarding);
  if (!next) return null;
  /*
    예전에는 solid info `Alert` 라, 모든 화면에서 이 안내가 접힘선 위 가장 진한 요소였어요 —
    남은 설정 한 단계가 그 화면이 하려던 일보다 크게 보였습니다. 문구·링크는 그대로 두고
    무게만 낮춥니다(테두리 + 한 단 낮춘 바탕, 강조는 아이콘 색 하나).
  */
  return (
    <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-line-alternative bg-background-alternative px-4 py-3">
      <span className="inline-flex shrink-0 text-primary">
        <Icon name="circleInfo" size={16} />
      </span>
      <span className="shrink-0 text-label-1 text-label-strong">설정이 조금 남았어요</span>
      <span className="min-w-0 flex-1 text-label-2 text-label-alternative">{next.message}</span>
      <Link to={next.to} className="shrink-0">
        <TextButton size="small">{next.label}</TextButton>
      </Link>
    </div>
  );
}
